import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {Subject} from 'rxjs';
import {debounceTime, map, startWith, takeUntil} from 'rxjs/operators';
import * as utils from "../../lib/utils";
import {allStringFilter} from "../../lib/utils";
import {CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {DblClickCopyDirective} from "../../directives/dbl-click-copy.directive";
import {SharedModule} from "../../shared/modules/shared-module.module";

@Component({
    selector: 'select-many-search',
    templateUrl: 'select-many-search.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
        SharedModule,
        ReactiveFormsModule,
        DblClickCopyDirective,
    ],
})
export class SelectManySearchComponent implements OnInit, OnDestroy {
    description_map: { [id: string]: string } = {};

    @ViewChild('search') searchTextBox: ElementRef;
    selectFormControl: FormControl<any> = new FormControl();
    searchTextboxControl: FormControl<any> = new FormControl();

    emitSelected() {
        this.setSelectedValues();
        this.selectFormControl.patchValue(this.selectedValues);

        this.valueChange.emit(this.selectedValues);
        this.selectChange.emit(this.selectedValues);
    }

    /**
     * For running a function when the selection was changed.
     */
    @Output()
    selectChange: EventEmitter<any> = new EventEmitter<any>();

    /**
     * For binding to the input value;
     */
    @Output()
    valueChange: EventEmitter<any> = new EventEmitter<any>();
    private onDestroy: Subject<void> = new Subject();

    get disabled(): boolean {
        return this.selectFormControl.disabled;
    }

    @Input()
    set disabled(state: boolean) {
        if (state) {
            this.selectFormControl.disable();
        } else {
            this.selectFormControl.enable();
        }
    }

    /**
     * Object keys used for search.
     */
    @Input()
    data_schema: any[] = [
        "attributes.name",
        "attributes.description"
    ];

    /**
     *
     */
    @Input()
    fill_stubs = false;

    selectedValues: any[];
    select_all: boolean = false;

    // @Input()
    // data: any[] = [];
    _data: any[];

    get data(): any[] {
        return this._data;
    }

    @Input()
    set data(data: any[]) {
        this._data = data;
        if (data && data.length > 0) {
            data.forEach(item => {
                this.description_map[item.id] = this.stringFunction(item);
            });

            this.filteredOptions = utils.deepCopy(data);
        }
    }

    /**
     * Preselect items in the data list.
     * @param values
     */
    @Input()
    set value(values: any[]) {
        this.selectedValues = values;
        this.selectFormControl.patchValue(this.selectedValues);
        this.select_all = false;
    }

    /**
     * Comparator for comparing elements for data to values/selected.
     * @param item1
     * @param item2
     */
    @Input()
    compareItems = (item1: any, item2: any): boolean => {
        return item1 && item2 && item1.id === item2.id;
    }
    /*This keeps track of the actual number of selected values because the actual selectedValues needs to be filtered by the available data*/
    @Input() numberSelected: number;

    @Input()
    stringFunction: (obj: any) => string = (obj) => {
        let name_: string;
        let descrip_: string;

        if (obj.attributes && obj.attributes.name != undefined) {
            name_ = obj.attributes.name
        } else name_ = '';

        if (obj.attributes && obj.attributes.description != undefined) {
            descrip_ = obj.attributes.description
        } else return name_;

        return name_ + '-' + descrip_;
    };

    filteredOptions: any[];

    @Input() label: string = '';

    @Input() classes: string;


    ngOnInit() {
        if (!this.label) {
            this.classes += ' no-label';
        }
        if (this.selectedValues) {
            this.checkSelected();
        } else {
            this.selectedValues = [];
        }

        this.data.forEach(item => {
            this.description_map[item.id] = this.stringFunction(item);
        });

        this.setSelectedValues();
        this.selectFormControl.patchValue(this.selectedValues);

        setTimeout(() => {
            /**
             * Set filter event based on value changes
             */
            this.searchTextboxControl.valueChanges.pipe(
                takeUntil(this.onDestroy),
                debounceTime(300),
                startWith<string>(''),
                map(name => this.filterOptionList(name))
            ).subscribe(values => {
                this.filteredOptions = values;
                this.sortFiltered();
            });
        });
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }

    checkSelected() {
        const data_dict: {} = {};
        this.data.forEach((item) => data_dict[item.id] = item);
        // ensure the selected values exists in the available data
        this.selectedValues = this.selectedValues?.filter(item => !!data_dict[item.id]).map((item: any) => data_dict[item.id]);
    }

    sortFiltered(): void {
        if (!this.selectedValues || this.selectedValues.length < 1) {
            return;
        }
        this.filteredOptions = this.filteredOptions.sort((a, b) => {
            let order: number = 0;
            if (this.selectedValues.find(item => item.id === a.id)) {
                if (!this.selectedValues.find(item => item.id === b.id)) {
                    order = this.filteredOptions.length === this.data.length ? -1 : 1;
                }
            } else {
                if (this.selectedValues.find(item => item.id === b.id)) {
                    order = this.filteredOptions.length === this.data.length ? 1 : -1;
                }
            }
            if (order === 1 || order === -1) {
            }
            return order;
        });
    }

    filterValue: string;

    filterOptionList(filterString: string): any[] {
        this.filterValue = filterString?.toLowerCase();
        return allStringFilter(filterString, this.data);
    }

    selectAll() {
        if (this.select_all === true) {
            if (this.filteredOptions && this.filteredOptions.length > 0) {
                this.filteredOptions.forEach((e) => {
                    if (this.selectedValues.indexOf(e) === -1) {
                        this.selectedValues.push(e);
                    }
                });
            }
        } else {
            this.filteredOptions.forEach((e) => {
                let index = this.selectedValues.indexOf(e);
                if (index >= 0) {
                    this.selectedValues.splice(index, 1)
                }
            });
        }
        this.selectFormControl.patchValue(this.selectedValues);
        this.checkSelected();
        this.valueChange.emit(this.selectedValues);
        this.selectChange.emit(this.selectedValues);
    }

    selectionChange(event) {
        // * Remove from selected values based on uncheck - this is necessary if still using selectFromControl
        if (event.isUserInput && event.source.selected == false) {
            let index: number = this.selectedValues.indexOf(event.source.value);
            this.selectedValues.splice(index, 1)
        }
    }

    openedChange(e: boolean) {
        // Set search textbox value as empty while opening selectbox
        this.searchTextboxControl.patchValue('');
        this.select_all = false;
        // Focus to search textbox while clicking on selectbox
        if (e === true) {
            this.searchTextBox.nativeElement.focus();
        }
        this.checkSelected();
        this.sortFiltered();
    }

    /**
     * Clearing search textbox value
     */
    clearSearch() {
        this.filterValue = "";
        this.searchTextboxControl.patchValue('');
    }

    /**
     * Set selected values to retain the state
     */
    setSelectedValues() {
        //Note: this may need to be adjusted to accommodate matching on other attributes
        if (this.selectFormControl.value && this.selectFormControl.value.length > 0) {
            this.selectFormControl.value.forEach((e) => {
                if (!this.selectedValues.find(s => e.id === s.id)) {
                    this.selectedValues.push(e);
                }
            });
        }
    }

}

/**  Copyright 2018 Google Inc. All Rights Reserved.
 Use of this source code is governed by an MIT-style license that
 can be found in the LICENSE file at http://angular.io/license */
